import { Card } from '@/components/common/card'
import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import { CCTP_NETWORKS, useCctpBridge } from '@/hooks/useCctpBridge'
import { useDebounce } from '@/hooks/useDebounce'
import { CCTP_USDC_ADDRESSES, useCctpContextQuery } from '@/queries/cctpContext.query'
import { useCctpMoneyInTransitQuery } from '@/queries/cctpMoneyInTransit.query'
import { useCctpTransactionsQuery } from '@/queries/cctpTransactions.query'
import { useWalletBalancesQuery } from '@/queries/walletBalances.query'
import { getCctpTiming } from '@/utils/cctp/getCctpTiming'
import { cn } from '@/utils/cn'
import { isNumberFormat } from '@/utils/isNumberFormat'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { zodResolver } from '@hookform/resolvers/zod'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { addressSchema, networkNameSchema } from '@vaultsfyi/common/schemas'
import type { Network } from '@vaultsfyi/common/types'
import { DECIMAL_SCALE, formatAmount } from '@vaultsfyi/common/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { formatUnits, isAddressEqual, parseUnits } from 'viem'
import { z } from 'zod'
import { Checkbox } from '../common/checkbox'
import { Input } from '../common/input'
import { Label } from '../common/label'
import { ConnectModal } from '../connect/connectModal'
import { ArrowExpandedIcon } from '../icons/arrow'
import { AmountInput } from './amountInput'
import { BridgeOptions } from './bridgeOptions'
import { BridgeProgress } from './bridgeProgress'
import { ChainSelector } from './chainSelector'
import { InTransitItem } from './inTransitItem'

const cctpBridgeFormSchema = z
  .object({
    fromChain: networkNameSchema,
    toChain: networkNameSchema,
    amount: z.string(),
    sendToDifferentWallet: z.boolean(),
    destinationWallet: addressSchema.optional(),
    useForwarding: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.sendToDifferentWallet && !data.destinationWallet) return false
      return true
    },
    { message: 'Destination wallet is required', path: ['destinationWallet'] }
  )
  .refine((data) => data.fromChain !== data.toChain, {
    message: 'Origin and destination network cannot be the same',
    path: ['toChain'],
  })

type CctpBridgeFormData = z.infer<typeof cctpBridgeFormSchema>

export const CctpBridgeWidget = () => {
  const { account, chainId } = useAccountContext()
  const { trackEvent } = useMixpanel()
  const { data: walletBalances } = useWalletBalancesQuery({ address: account })
  const {
    isBridging,
    isClaiming,
    error: bridgeError,
    clearError,
    bridgeSteps,
    claimSteps,
    clearSteps,
    executeBridge,
    executeClaim,
  } = useCctpBridge()

  const getDefaultFromChain = useCallback((): Network => {
    if (!chainId) return 'base'
    const chainIdToNetwork: Record<number, Network> = { 1: 'mainnet', 42161: 'arbitrum', 8453: 'base' }
    return chainIdToNetwork[chainId] ?? 'base'
  }, [chainId])

  const defaultFromChain = useMemo(() => getDefaultFromChain(), [getDefaultFromChain])
  const defaultToChain: Network = defaultFromChain === 'base' ? 'mainnet' : 'base'

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CctpBridgeFormData>({
    resolver: zodResolver(cctpBridgeFormSchema),
    defaultValues: {
      fromChain: defaultFromChain,
      toChain: defaultToChain,
      amount: '',
      sendToDifferentWallet: false,
      destinationWallet: undefined,
      useForwarding: false,
    },
  })

  const fromChain = watch('fromChain')
  const toChain = watch('toChain')
  const amount = watch('amount')
  const sendToDifferentWallet = watch('sendToDifferentWallet')
  const destinationWallet = watch('destinationWallet')
  const useForwarding = watch('useForwarding')

  const [debouncedAmount, setDebouncedAmount] = useState(amount)
  const [debouncedDestinationWallet, setDebouncedDestinationWallet] = useState(destinationWallet)

  useDebounce(
    () => {
      if (amount !== debouncedAmount) {
        setDebouncedAmount(amount)
      }
    },
    [amount],
    SECOND_IN_MILLISECONDS / 2
  )

  useDebounce(() => setDebouncedDestinationWallet(destinationWallet), [destinationWallet], SECOND_IN_MILLISECONDS / 2)

  const usdcBalance = useMemo(() => {
    if (!walletBalances || !fromChain) return null
    const nativeUsdcAddress = CCTP_USDC_ADDRESSES[fromChain]
    const item = walletBalances.find(
      (b) => b.network.name === fromChain && !!nativeUsdcAddress && isAddressEqual(b.address, nativeUsdcAddress)
    )
    return item
      ? {
          balance: item.balanceNative,
          decimals: item.decimals,
          balanceFormatted: formatUnits(item.balanceNative, item.decimals),
        }
      : null
  }, [walletBalances, fromChain])

  const nativeUsdcBalances = useMemo(
    () =>
      walletBalances?.filter((b) => {
        const nativeUsdcAddress = CCTP_USDC_ADDRESSES[b.network.name]
        return !!nativeUsdcAddress && isAddressEqual(b.address, nativeUsdcAddress)
      }),
    [walletBalances]
  )

  const usdcDecimals = usdcBalance?.decimals ?? 6

  const parsedAmount = useMemo(() => {
    if (!debouncedAmount || Number(debouncedAmount) <= 0) return '0'
    try {
      return parseUnits(debouncedAmount, usdcDecimals).toString()
    } catch {
      return '0'
    }
  }, [debouncedAmount, usdcDecimals])

  const { data: context, isFetching: isContextFetching } = useCctpContextQuery({
    userAddress: account,
    srcNetwork: fromChain,
    dstNetwork: toChain,
  })

  const sharedBridgeTxArgs = {
    action: 'bridge' as const,
    userAddress: account,
    srcNetwork: fromChain,
    dstNetwork: toChain,
    amount: parsedAmount,
    useForwarding,
    recipientAddress: sendToDifferentWallet ? debouncedDestinationWallet : undefined,
    enabled: parsedAmount !== '0',
  }

  const { data: fastBridgeActions, isFetching: isFastTxFetching } = useCctpTransactionsQuery({
    ...sharedBridgeTxArgs,
    transferType: 'fast',
  })

  const { data: standardBridgeActions, isFetching: isStandardTxFetching } = useCctpTransactionsQuery({
    ...sharedBridgeTxArgs,
    transferType: 'standard',
  })

  const fees = useMemo(() => {
    if (!context?.fees) return null
    const forwardingFeeUSDC = useForwarding
      ? Number(formatUnits(BigInt(context.fees.forwardingFeeNative), usdcDecimals))
      : 0
    const numericAmount = Number(debouncedAmount) || 0
    return {
      fast: numericAmount * (context.fees.fastTransferFeeBps / DECIMAL_SCALE) + forwardingFeeUSDC,
      standard: numericAmount * (context.fees.standardTransferFeeBps / DECIMAL_SCALE) + forwardingFeeUSDC,
      forwardingFeeUSDC,
    }
  }, [context, debouncedAmount, useForwarding])

  const hasInsufficientBalance = useMemo(() => {
    if (!amount || !usdcBalance) return false
    return Number(amount) > Number(usdcBalance.balanceFormatted)
  }, [amount, usdcBalance])

  const handleNetworkChange = useCallback(
    (field: 'fromChain' | 'toChain', value: Network) => {
      setValue(field, value, { shouldValidate: true })
      trackEvent('CCTP network changed', {
        Direction: field === 'fromChain' ? 'Origin' : 'Destination',
        Network: value,
      })
    },
    [setValue, trackEvent]
  )

  const handleMaxClick = useCallback(() => {
    if (usdcBalance) {
      setValue('amount', usdcBalance.balanceFormatted, { shouldValidate: true })
    }
  }, [setValue, usdcBalance])

  const handleBridge = useCallback(
    (type: 'fast' | 'standard') =>
      handleSubmit(async () => {
        const actions = type === 'fast' ? fastBridgeActions : standardBridgeActions
        if (!actions) return
        clearError()
        const bridgeProps = {
          Type: type,
          Amount: `${debouncedAmount} USDC`,
          OriginNetwork: fromChain,
          DestinationNetwork: toChain,
          WalletAddress: account,
          RecipientAddress: sendToDifferentWallet ? debouncedDestinationWallet : account,
          Forwarding: useForwarding,
          Fee: type === 'fast' ? fees?.fast : fees?.standard,
        }
        trackEvent('CCTP bridge initiated', bridgeProps)
        const result = await executeBridge(actions, fromChain)
        if (result.status === 'success') {
          trackEvent('CCTP bridge successful', { ...bridgeProps, TxHash: result.txHash })
          setValue('amount', '', { shouldValidate: false })
        } else if (result.status === 'error') {
          trackEvent('CCTP bridge failed', { ...bridgeProps, Error: result.message })
        }
      })(),
    [
      handleSubmit,
      fastBridgeActions,
      standardBridgeActions,
      executeBridge,
      fromChain,
      toChain,
      account,
      sendToDifferentWallet,
      debouncedDestinationWallet,
      useForwarding,
      debouncedAmount,
      fees,
      clearError,
      setValue,
      trackEvent,
    ]
  )

  const fastTiming = getCctpTiming(fromChain, toChain, 'fast')
  const standardTiming = getCctpTiming(fromChain, toChain, 'standard')

  const bridgeOptionsData = {
    fastBridge: {
      type: 'fast' as const,
      estimateSpeed: fastTiming.estimatedTime,
      confirmation: `${fastTiming.blocks} ${fastTiming.blocks === '1' ? 'block' : 'blocks'}`,
      feeAmount:
        fees && debouncedAmount
          ? formatAmount(fees.fast, { minimumFractionDigits: 0, maximumFractionDigits: 6 })
          : undefined,
      receiveAmount:
        fees && debouncedAmount
          ? formatAmount(Number(debouncedAmount) - fees.fast, { minimumFractionDigits: 0, maximumFractionDigits: 6 })
          : undefined,
    },
    standardBridge: {
      type: 'standard' as const,
      estimateSpeed: standardTiming.estimatedTime,
      confirmation: `${standardTiming.blocks} ${standardTiming.blocks === '1' ? 'block' : 'blocks'}`,
      feeAmount:
        fees && debouncedAmount
          ? formatAmount(fees.standard, { minimumFractionDigits: 0, maximumFractionDigits: 6 })
          : undefined,
      receiveAmount:
        fees && debouncedAmount
          ? formatAmount(Number(debouncedAmount) - fees.standard, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 6,
            })
          : undefined,
    },
  }

  const isTxFetching = isFastTxFetching || isStandardTxFetching

  const { data: moneyInTransitData } = useCctpMoneyInTransitQuery(account)

  const pendingRoutes = useMemo(
    () =>
      moneyInTransitData?.routes.flatMap((route) =>
        route.moneyInTransit
          .filter((item) => item.status === 'pending' || item.status === 'attested')
          .map((item) => ({ item, srcNetwork: route.srcNetwork.name, dstNetwork: route.dstNetwork.name }))
      ) ?? [],
    [moneyInTransitData]
  )

  useEffect(() => {
    if (debouncedAmount && Number(debouncedAmount) > 0) {
      trackEvent('CCTP amount entered', {
        Amount: `${debouncedAmount} USDC`,
        OriginNetwork: fromChain,
        DestinationNetwork: toChain,
      })
    }
  }, [debouncedAmount, fromChain, toChain, trackEvent])

  return (
    <div className="mx-auto w-full max-w-3xl">
      <Card>
        <div className="flex flex-col gap-4 p-4 md:gap-5 md:p-5">
          <h2 className="text-center font-bold text-heading-foreground text-xl md:text-2xl">CCTP Bridge</h2>
          <p className="text-center text-sm md:text-base">
            <span className="font-semibold">Move USDC between chains with zero fees.</span>
            <br />
            Powered by Circle's native CCTP infrastructure.
          </p>

          <form onSubmit={handleSubmit(() => {})} className="flex flex-col gap-4 md:gap-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-4 sm:grid sm:grid-cols-[1fr,auto,1fr] sm:items-end">
                <ChainSelector
                  chains={CCTP_NETWORKS}
                  selectedChain={fromChain}
                  onChange={(chain) => handleNetworkChange('fromChain', chain)}
                  label="From"
                  disabled={isBridging}
                  balances={nativeUsdcBalances}
                />
                <div className="mt-auto hidden h-6 w-6 items-center justify-center sm:flex sm:h-12">
                  <ArrowExpandedIcon size={24} className="max-sm:rotate-90" />
                </div>
                <ChainSelector
                  chains={CCTP_NETWORKS}
                  selectedChain={toChain}
                  onChange={(chain) => handleNetworkChange('toChain', chain)}
                  label="To"
                  disabled={isBridging}
                  balances={nativeUsdcBalances}
                  className={cn(errors.toChain && 'border-destructive hocus:border-destructive')}
                />
              </div>
              {errors.toChain && <p className="text-destructive text-sm">{errors.toChain.message}</p>}
            </div>

            <AmountInput
              {...register('amount', {
                onChange: (e) => {
                  const inputValue = e.target.value
                  if (isNumberFormat(inputValue)) {
                    setValue('amount', inputValue, { shouldValidate: true })
                  } else {
                    setValue('amount', amount)
                  }
                },
              })}
              error={errors.amount?.message}
              onMaxClick={handleMaxClick}
              balance={usdcBalance?.balance}
              decimals={usdcDecimals}
              disabled={isBridging}
            />

            <div className="flex flex-col gap-4">
              <Label className="flex items-start gap-2 sm:items-center">
                <Checkbox
                  checked={sendToDifferentWallet}
                  onCheckedChange={(checked) => {
                    const enabled = checked === true
                    setValue('sendToDifferentWallet', enabled)
                    trackEvent('CCTP different wallet toggled', { Enabled: enabled })
                  }}
                  disabled={isBridging}
                />
                <span className="text-body-foreground text-sm">
                  Send to a different wallet on <span className="capitalize">{getNetworkName(toChain)}</span>
                </span>
              </Label>

              {sendToDifferentWallet && (
                <div className="flex flex-col gap-2">
                  <Label className="font-medium text-body-foreground text-sm">Destination Wallet</Label>
                  <Input
                    {...register('destinationWallet')}
                    placeholder="0x..."
                    className={cn('h-12', errors.destinationWallet && 'border-destructive hocus:border-destructive')}
                    disabled={isBridging}
                  />
                  {errors.destinationWallet && (
                    <p className="text-destructive text-sm">{errors.destinationWallet.message}</p>
                  )}
                </div>
              )}

              <Label className="flex items-start gap-2 sm:items-center">
                <Checkbox
                  checked={useForwarding}
                  onCheckedChange={(checked) => {
                    const enabled = checked === true
                    setValue('useForwarding', enabled)
                    trackEvent('CCTP forwarding toggled', { Enabled: enabled })
                  }}
                  disabled={isBridging}
                />
                <span className="text-body-foreground text-sm">
                  Automatically claim bridged assets on arrival
                  {useForwarding && fees?.forwardingFeeUSDC !== undefined && fees.forwardingFeeUSDC > 0 && (
                    <span className="ml-1">
                      (~
                      {formatAmount(fees.forwardingFeeUSDC, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{' '}
                      USDC fee)
                    </span>
                  )}
                </span>
              </Label>
            </div>

            {bridgeSteps.length > 0 && (
              <BridgeProgress steps={bridgeSteps} isActive={isBridging} onClose={clearSteps} />
            )}

            <div className="flex flex-col gap-2">
              {hasInsufficientBalance && <p className="text-destructive text-sm">Insufficient USDC balance</p>}
              {bridgeError && <p className="text-destructive text-sm">{bridgeError}</p>}

              {!account ? (
                <ConnectModal action="connect" triggerVariant="primary" className="w-full" />
              ) : (
                <BridgeOptions
                  fastBridge={bridgeOptionsData.fastBridge}
                  standardBridge={bridgeOptionsData.standardBridge}
                  onSelectBridge={handleBridge}
                  loading={isContextFetching && !fees}
                  disabled={
                    !parsedAmount || parsedAmount === '0' || isTxFetching || hasInsufficientBalance || isBridging
                  }
                  bridging={isBridging}
                />
              )}
            </div>
          </form>

          {pendingRoutes.length > 0 && (
            <div className="flex flex-col gap-3 border-t pt-4">
              <p className="font-medium text-body-foreground text-sm">Pending transfers</p>
              <div className="flex flex-col gap-2">
                {pendingRoutes.map(({ item, srcNetwork, dstNetwork }) => (
                  <InTransitItem
                    key={item.message ?? `${srcNetwork}-${dstNetwork}-${item.amountNative}-${item.mintRecipient}`}
                    item={item}
                    srcNetwork={srcNetwork}
                    dstNetwork={dstNetwork}
                    decimals={usdcDecimals}
                    account={account}
                    executeClaim={executeClaim}
                    isClaiming={isClaiming}
                  />
                ))}
              </div>
              {claimSteps.length > 0 && (
                <BridgeProgress steps={claimSteps} isActive={isClaiming} onClose={clearSteps} />
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
